import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { addMonths, calendarGrid, isoDayInRange, parseIsoDay, todayIsoDay } from '../lib/calendar';
import { formatIsoDate } from '../lib/dateFormat';
import { useLanguage } from '../lib/i18n/LanguageContext';

/**
 * Calendar day picker that always *displays* DD/MM/YYYY.
 *
 * Native `<input type="date">` paints its popup in the OS chrome — square
 * corners, no `border-radius` — the same reason selects became `SelectMenu`.
 * The closed field stays a rounded form control; the open calendar is our
 * own panel, portaled so ancestor overflow cannot clip it.
 */
export function DateField({
  id,
  value,
  onChange,
  min,
  max,
  rangeStart,
  rangeEnd,
  'aria-label': ariaLabel,
}: {
  id?: string;
  value: string;
  onChange: (isoDay: string) => void;
  min?: string;
  max?: string;
  /** Partner start of a range — highlighted in the calendar; opening jumps to this month. */
  rangeStart?: string;
  /** Partner end of a range — highlighted when picking the start date. */
  rangeEnd?: string;
  'aria-label'?: string;
}) {
  const { dict } = useLanguage();
  const copy = dict.common.datePicker;
  const [open, setOpen] = useState(false);
  const selected = parseIsoDay(value);
  const [view, setView] = useState(() => ({
    year: selected?.year ?? new Date().getFullYear(),
    month: selected?.month ?? new Date().getMonth() + 1,
  }));
  const [placement, setPlacement] = useState<{ top: number; left: number; openUp: boolean } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dialogId = useId();
  const today = todayIsoDay();
  const cells = useMemo(() => calendarGrid(view.year, view.month), [view.year, view.month]);

  useEffect(() => {
    if (!open) return;
    const current = parseIsoDay(value);
    const start = parseIsoDay(rangeStart ?? '');
    const focus = start ?? current;
    if (focus) setView({ year: focus.year, month: focus.month });
  }, [open, value, rangeStart]);

  useLayoutEffect(() => {
    if (!open) return;
    const reposition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const panelWidth = panelRef.current?.offsetWidth ?? 292;
      const panelHeight = panelRef.current?.offsetHeight ?? 0;
      const gap = 6;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < panelHeight + gap && spaceAbove > spaceBelow;
      let left = rect.left;
      if (left + panelWidth > window.innerWidth - 8) left = window.innerWidth - panelWidth - 8;
      if (left < 8) left = 8;
      setPlacement({
        top: openUp ? rect.top - gap : rect.bottom + gap,
        left,
        openUp,
      });
    };
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, view.year, view.month]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function pick(iso: string) {
    if (!isoDayInRange(iso, min, max)) return;
    onChange(iso);
    setOpen(false);
  }

  const todayEnabled = isoDayInRange(today, min, max);
  const monthLabel = `${copy.months[view.month - 1]} ${view.year}`;

  return (
    <div className={`date-field${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        className="date-field-display"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={dialogId}
        aria-label={ariaLabel}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
      >
        {formatIsoDate(value)}
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="date-picker-panel"
            role="dialog"
            id={dialogId}
            aria-label={ariaLabel}
            style={{
              position: 'fixed',
              top: placement ? placement.top : -9999,
              left: placement ? placement.left : -9999,
              transform: placement?.openUp ? 'translateY(-100%)' : undefined,
              visibility: placement ? 'visible' : 'hidden',
            }}
          >
            <div className="date-picker-head">
              <button
                type="button"
                className="date-picker-nav"
                aria-label={copy.previousMonth}
                onClick={() => setView((v) => addMonths(v.year, v.month, -1))}
              >
                <span aria-hidden="true">‹</span>
              </button>
              <p className="date-picker-month">{monthLabel}</p>
              <button
                type="button"
                className="date-picker-nav"
                aria-label={copy.nextMonth}
                onClick={() => setView((v) => addMonths(v.year, v.month, 1))}
              >
                <span aria-hidden="true">›</span>
              </button>
            </div>
            <div className="date-picker-weekdays" aria-hidden="true">
              {copy.weekdays.map((d, i) => (
                <span key={i}>{d}</span>
              ))}
            </div>
            <div className="date-picker-grid" role="grid">
              {cells.map((cell) => {
                const disabled = !isoDayInRange(cell.iso, min, max);
                const isSelected = cell.iso === value;
                const isAnchor =
                  !isSelected &&
                  (cell.iso === rangeStart || cell.iso === rangeEnd);
                const isToday = cell.iso === today;
                return (
                  <button
                    key={cell.iso}
                    type="button"
                    role="gridcell"
                    aria-selected={isSelected}
                    disabled={disabled}
                    className={[
                      'date-picker-day',
                      cell.inMonth ? '' : 'is-outside',
                      isSelected ? 'is-selected' : '',
                      isAnchor ? 'is-range-anchor' : '',
                      isToday && !isSelected && !isAnchor ? 'is-today' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => pick(cell.iso)}
                  >
                    {cell.day}
                  </button>
                );
              })}
            </div>
            <div className="date-picker-foot">
              <button
                type="button"
                className="date-picker-today"
                disabled={!todayEnabled}
                onClick={() => pick(today)}
              >
                {copy.today}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
